import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  parseCompositionToPortfolioSummary,
  parseCompositionToRealEconomyAssetSummary,
  parseCompositionToRealEconomyProductConcentration,
  parseResidueDuration,
  parseRiskProfile,
} from '../../../../api/parsers/wallet-parsers';
import {
  FileDownloadedType,
  ProductDetails,
  ProductPerformanceList,
  Wallet,
  WalletSummary,
} from '../../../../model';
import { DownloaderMediaType } from '../../../../model/downloader-media-type';
import { FileDownloaded } from '../../../../model/file-downloaded';
import { ProductUnderlyingGroupsPaginated } from '../../../../model/wallet/details/product-underlying-groups-paginated';
import {
  PortfolioSummaryByMacroCategory,
  RealEconomyAssetSummary,
  ResidueDuration,
  RiskProfile,
  RealEconomyProductsConcentration,
} from '../../../../model/wallet/wallet-ow';
import walletRepository from '../../../../repositories/wallet/wallet-repository';
import {
  createWalletPayloadAction,
  getPendingMeta,
} from '../../../../utils/api-utils';
import { AsyncThunkConfig } from '../../../async-thunk-config';
import { WalletError, WalletPayloadAction } from './my-wallet-types';

export const retrieveWallet = createAsyncThunk<
  WalletPayloadAction<{ wallet: Wallet; summary: WalletSummary }>,
  void,
  AsyncThunkConfig<WalletPayloadAction<WalletError>>
>(
  'retrieve-wallet',
  async (_, thunkApi) => {
    try {
      return await walletRepository.getWallet(thunkApi);
    } catch (error) {
      console.error(error);
      return thunkApi.rejectWithValue(
        createWalletPayloadAction('GENERIC_ERROR', thunkApi)
      );
    }
  },
  {
    getPendingMeta,
  }
);

export const retrieveProductsPerformances = createAsyncThunk<
  WalletPayloadAction<ProductPerformanceList>,
  void,
  AsyncThunkConfig<WalletPayloadAction<WalletError>>
>(
  'retrieve-products-performances',
  async (_, thunkApi) => {
    try {
      return await walletRepository.getPerformance(thunkApi);
    } catch (error) {
      console.error(error);
      return thunkApi.rejectWithValue(
        createWalletPayloadAction('GENERIC_ERROR', thunkApi)
      );
    }
  },
  {
    getPendingMeta,
  }
);

export const retrieveProductTransactions = createAsyncThunk<
  WalletPayloadAction<
    Omit<ProductDetails, 'contract' | 'category' | 'underlyingGroups'>
  >,
  void,
  AsyncThunkConfig<WalletPayloadAction<WalletError>>
>(
  'retrieve-product-transactions',
  async (_, thunkApi) => {
    try {
      return await walletRepository.getTransactions(thunkApi);
    } catch (error) {
      console.error(error);
      return thunkApi.rejectWithValue(
        createWalletPayloadAction('GENERIC_ERROR', thunkApi)
      );
    }
  },
  {
    getPendingMeta,
  }
);

export const retrieveProductUnderlying = createAsyncThunk<
  WalletPayloadAction<ProductUnderlyingGroupsPaginated>,
  void,
  AsyncThunkConfig<WalletPayloadAction<WalletError>>
>(
  'retrieve-product-underlying',
  async (_, thunkApi) => {
    try {
      return await walletRepository.getUnderlying(thunkApi);
    } catch (error) {
      console.error(error);
      return thunkApi.rejectWithValue(
        createWalletPayloadAction('GENERIC_ERROR', thunkApi)
      );
    }
  },
  {
    getPendingMeta,
  }
);

export const downloadWalletFile = createAsyncThunk<
  WalletPayloadAction<FileDownloaded>,
  { mediaType: DownloaderMediaType; type: FileDownloadedType },
  AsyncThunkConfig<WalletPayloadAction<WalletError>>
>(
  'download-wallet-file',
  async (req, thunkApi) => {
    try {
      if (req.type === 'performance') {
        const file = await walletRepository.downloadPerformanceFile(
          thunkApi,
          req
        );
        return createWalletPayloadAction(file, thunkApi);
      } else if (req.type === 'holdings') {
        const file = await walletRepository.downloadHoldingsFile(thunkApi, req);
        return createWalletPayloadAction(file, thunkApi);
      } else if (req.type === 'transaction') {
        const file = await walletRepository.downloadTransactionsFile(
          thunkApi,
          req
        );
        return createWalletPayloadAction(file, thunkApi);
      } else if (req.type === 'underlying') {
        const file = await walletRepository.downloadUnderlyingFile(
          thunkApi,
          req
        );
        return createWalletPayloadAction(file, thunkApi);
      } else if (req.type === 'pir') {
        const file = await walletRepository.downloadPirFile(thunkApi, req);
        return createWalletPayloadAction(file, thunkApi);
      } else if (req.type === 'realEconomyDetail') {
        const file = await walletRepository.downloadRealEconomyDetailFile(thunkApi, req);
        return createWalletPayloadAction(file, thunkApi);
      }

      return thunkApi.rejectWithValue(
        createWalletPayloadAction('GENERIC_ERROR', thunkApi)
      );
    } catch (error) {
      console.error(error);
      return thunkApi.rejectWithValue(
        createWalletPayloadAction('GENERIC_ERROR', thunkApi)
      );
    }
  },
  {
    getPendingMeta,
  }
);

export const retrievePortfolioCompositionMacroCategory = createAsyncThunk<
  PortfolioSummaryByMacroCategory,
  void,
  AsyncThunkConfig<WalletError>
>('retrieve-real-economy-composition-macrocategory', async (_, thunkApi) => {
  try {
    const result = await walletRepository.getErPortfolioCompositionByType(
      'PRODUCTMACROCATEGORY',
      thunkApi
    );
    return parseCompositionToPortfolioSummary(result);
  } catch (error) {
    console.error(error);
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

export const retrieveRealEconomyAssetSummary = createAsyncThunk<
  RealEconomyAssetSummary,
  void,
  AsyncThunkConfig<WalletError>
>('retrieve-real-economy-asset-summary', async (_, thunkApi) => {
  try {
    const result = await walletRepository.getErPortfolioCompositionByType(
      'PRODUCTTYPE',
      thunkApi
    );

    return parseCompositionToRealEconomyAssetSummary(result);
  } catch (error) {
    console.error(error);
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});
export const retrieveRealEconomyResidueDuration = createAsyncThunk<
  ResidueDuration,
  void,
  AsyncThunkConfig<WalletError>
>('retrieve-real-economy-composition-duration', async (_, thunkApi) => {
  try {
    const result = await walletRepository.getErPortfolioCompositionByType(
      'DURATION',
      thunkApi
    );
    return parseResidueDuration(result);
  } catch (error) {
    console.error(error);
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

export const retrieveRealEconomyRiskProfile = createAsyncThunk<
  RiskProfile,
  void,
  AsyncThunkConfig<WalletError>
>('retrieve-real-economy-risk-profile', async (_, thunkApi) => {
  try {
    const result = await walletRepository.getErEvaluateMonitoring(thunkApi);
    return parseRiskProfile(result);
  } catch (error) {
    console.error(error);
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

export const retrieveRealEconomyProductsConcentration = createAsyncThunk<
  RealEconomyProductsConcentration,
  void,
  AsyncThunkConfig<WalletError>
>('retrieve-real-economy-product-concentration', async (_, thunkApi) => {
  try {
    const result = await walletRepository.getErPortfolioCompositionByType(
      'PRODUCT',
      thunkApi
    );

    return parseCompositionToRealEconomyProductConcentration(result);
  } catch (error) {
    console.error(error);
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});
